import clsx from 'clsx';
import { Link } from 'react-router';
import { Icon } from './Icon';

const siteLinks = [
  { label: 'Нүүр хуудас', href: '/' },
  { label: 'Бидний тухай', href: '/about' },
  { label: 'Тэмцээн, уралдаан', href: '/competitions' },
  { label: 'Мэдээ мэдээлэл', href: '/blogs' },
  { label: 'Галлерей', href: '/gallery' },
  { label: 'Холбоо барих', href: '/contact' },
];

function SocialLink({ className, href, icon }: { className?: string; href: string; icon: any }) {
  return (
    <Link
      className={clsx(
        'flex h-10 w-10 items-center justify-center rounded-full bg-green-500 duration-300 ease-in-out hover:bg-green-600',
        className,
      )}
      to={href}
    >
      <Icon icon={icon} className="h-5 w-5 text-white" />
    </Link>
  );
}

export const Footer = ({ contact }: { contact: any }) => {
  return (
    <footer className="space-y-8 divide-y divide-green-400/20 bg-yellow-100 px-4 pt-16 sm:px-6 sm:pt-20 lg:px-8">
      <div className="mx-auto grid max-w-md gap-y-8 sm:max-w-none sm:grid-cols-2 sm:gap-x-8 sm:gap-y-12 md:gap-x-12 lg:max-w-screen-2xl lg:grid-cols-11 lg:gap-8 xl:gap-12">
        {/* Block 1 */}
        <div className="flex flex-col lg:col-span-4 lg:mx-auto">
          <div className="flex items-center">
            <div className="w-60 flex-shrink-0 flex-grow-0">
              <Link to="/">
                <img src={'/images/logo.png'} alt="Шинэ Өнөөдөр Сургууль" className="h-auto" />
              </Link>
            </div>
          </div>
          <div className="mt-6 text-lg text-green-800">
            Эрүүл биетэй, саруул ухаантай, сайхан сэтгэлтэй сурагчдыг бэлтгэхэд бидний зорилго
            оршино
          </div>
          <div className="mt-5 w-full lg:mt-6">
            <div className="flex justify-start space-x-4">
              {contact.facebook && <SocialLink href={contact.facebook} icon="facebook" />}
              {contact.instagram && <SocialLink href={contact.instagram} icon="instagram" />}
              {contact.youtube && <SocialLink href={contact.youtube} icon="youtube" />}
            </div>
          </div>
        </div>
        {/* Block 2 */}
        <div className="flex-shrink sm:order-3 lg:order-none lg:col-span-2">
          <h6 className="relative text-xl font-bold tracking-wide text-green-900">
            <span className="relative z-20">Хөтөлбөрүүд</span>
            <span className="absolute -bottom-1 left-0 z-10 h-1 w-12 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500" />
          </h6>
          <ul className="mt-6 divide-y divide-green-400/20 text-lg">
            {[
              { to: '/programs/prep-class', label: 'Бэлтгэл бүлэг' },
              { to: '/programs/general-class', label: 'Ерөнхий эрдэм' },
              { to: '/programs/summer-camp', label: 'Хөдөлмөр зуслан' },
              { to: '/programs/club-info', label: 'Дугуйлан секц' },
            ].map((item, index) => (
              <li
                key={item.label}
                className={clsx(
                  'font-medium text-green-700 duration-300 ease-in-out hover:text-green-600',
                  index === 0 ? 'pb-2' : 'py-2',
                )}
              >
                <Link to={item.to}>{item.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        {/* Block 3 */}
        <div className="flex-shrink sm:order-4 lg:order-none lg:col-span-2">
          <h6 className="relative text-xl font-bold tracking-wide text-green-900">
            <span className="relative z-20">Түргэн холбоос</span>
            <span className="absolute -bottom-1 left-0 z-10 h-1 w-12 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500" />
          </h6>
          <ul className="mt-6 divide-y divide-green-400/20 text-lg">
            {siteLinks.map((link, index) => (
              <li
                key={`footer-site-link-${link.label}`}
                className={clsx(
                  'font-medium text-green-700 duration-300 ease-in-out hover:text-green-600',
                  index === 0 && 'pb-2',
                  index === siteLinks.length && 'pt-2',
                  index > 0 && index < siteLinks.length && 'py-2',
                )}
              >
                <Link to={link.href}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        {/* Block 4 */}
        <div className="sm:order-2 lg:order-none lg:col-span-3 lg:mx-auto">
          <h6 className="relative text-xl font-bold tracking-wide text-green-900">
            <span className="relative z-20">Холбоо барих</span>
            <span className="absolute -bottom-1 left-0 z-10 h-1 w-12 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500" />
          </h6>
          <ul className="mt-6 flex flex-col space-y-5">
            <li className="flex max-w-xs flex-shrink">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-400">
                <Icon icon="mapPin" className="h-6 w-6 text-green-700" />
              </span>
              <div className="ml-3 mt-0 flex-1 xl:ml-4">
                <h5 className="flex items-center text-base font-semibold text-green-900">Хаяг</h5>
                <p className="mt-0.5 text-sm leading-relaxed text-green-800 text-opacity-90">
                  {contact.address}
                </p>
              </div>
            </li>
            <li className="flex flex-shrink-0">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-green-200">
                <Icon icon="mail" className="h-6 w-6 text-green-700" />
              </span>
              <div className="ml-3 flex-1 xl:ml-4">
                <h5 className="flex items-center text-base font-semibold text-green-900">И-мэйл</h5>
                <p className="mt-0.5 text-sm leading-relaxed text-green-800 text-opacity-90">
                  {contact.email}
                </p>
              </div>
            </li>
            <li className="flex flex-shrink-0">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-200">
                <Icon icon="phone" className="h-6 w-6 text-green-700" />
              </span>
              <div className="ml-3 flex-1 xl:ml-4">
                <h5 className="flex items-center text-base font-semibold text-green-900">Утас</h5>
                <p className="mt-0.5 text-sm leading-relaxed text-green-800 text-opacity-90">
                  {contact.phone}
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className="mx-auto flex max-w-md flex-col justify-between py-8 sm:max-w-none sm:flex-row lg:max-w-screen-2xl">
        <span className="text-base text-green-800/90">
          © {new Date().getFullYear()} Шинэ Өнөөдөр сургууль. Бүх эрх хуулиар хамгаалагдсан.
        </span>
        <p className="mt-0.5 flex items-center text-green-800/90">
          Сургуулийн багш сургачид хамтран бүтээв
        </p>
      </div>
    </footer>
  );
};
